<template>
    <central-modal
        v-model:visible="showPageCommunityModal"
        header="Notifications"
        :size="{ width: '110rem', height: '95vh' }"
        @hide="closePageCommunityModal"
    >
        <div class="page-community-modal">
            <div class="page-community-modal-header">
                <div class="page-community-modal-heading">
                    <h2>Notifications hub</h2>
                    <p>{{ activePageCommunityDescription }}</p>
                </div>
                <div class="page-community-modal-actions flex-row-with-gap">
                    <div>
                        <primevue-button v-if="userAccessStore.canCreateCommunityThread" class="plain-btn" @click="openCreateCommunityThread">
                            Add content
                            <font-awesome-icon :icon="['fas', 'plus']" />
                        </primevue-button>
                    </div>
                    <div>
                        <primevue-button v-if="pageCommunityTab !== 'enactNotifications'" class="plain-btn" @click="goToRelevantCommunityPage">
                            {{ relevantCommunityPageLabel }}
                            <font-awesome-icon :icon="['fas', 'arrow-right']" />
                        </primevue-button>
                        <primevue-button v-else class="plain-btn" @click="goToNotificationsManager">
                            Go to enact notifications manager
                            <font-awesome-icon :icon="['fas', 'arrow-right']" />
                        </primevue-button>
                    </div>
                </div>
            </div>

            <tabs v-model:value="pageCommunityTab" class="page-community-tabs">
                <tab-list>
                    <tab value="yourFeed">
                        <span class="community-tab-label">
                            Your feed
                            <count-badge :count="newUserFeedDocumentCount" aria-label="New Your feed count" />
                        </span>
                    </tab>
                    <tab value="pageFeed">
                        <span class="community-tab-label">
                            Page feed
                            <count-badge :count="newPageFeedDocumentCount" aria-label="New Page feed count" />
                        </span>
                    </tab>
                    <tab value="pageFeatures">
                        <span class="community-tab-label">
                            Page features
                            <count-badge :count="newPageFeatureDocumentCount" aria-label="New Page features count" />
                        </span>
                    </tab>
                    <tab value="enactNotifications" :disabled="notificationsDisabled">
                        <span class="community-tab-label">
                            Enact notifications
                            <font-awesome-icon v-if="notificationsDisabled" :icon="['fas', 'lock']" class="community-tab-lock" />
                            <count-badge v-else :count="unseenNotifications" aria-label="Unseen Enact notifications count" />
                        </span>
                    </tab>
                </tab-list>

                <tab-panels>
                    <tab-panel value="yourFeed">
                        <div class="page-community-tab-content">
                            <document-items-list
                                v-if="pageCommunityTab === 'yourFeed'"
                                :documents="userCommunityDocuments"
                                list-layout="full"
                                :is-loading="isPageCommunityLoading"
                                :is-document-unread="isCommunityDocumentUnread"
                                :initial-visible-count="INITIAL_VISIBLE_DOCUMENT_COUNT"
                                :load-more-count="20"
                                document-route-name="deltaGptDocument"
                                open-mode="emit"
                                empty-message="No content is relevant to your access yet."
                                @open-document="openPageCommunityDocument"
                            />
                        </div>
                    </tab-panel>
                    <tab-panel value="pageFeed">
                        <div class="page-community-tab-content">
                            <document-items-list
                                v-if="pageCommunityTab === 'pageFeed'"
                                :documents="pageCommunityDocuments"
                                list-layout="full"
                                :is-loading="isPageCommunityLoading"
                                :is-document-unread="isCommunityDocumentUnread"
                                :initial-visible-count="INITIAL_VISIBLE_DOCUMENT_COUNT"
                                :load-more-count="20"
                                document-route-name="deltaGptDocument"
                                open-mode="emit"
                                empty-message="No Community threads are linked to this page yet."
                                @open-document="openPageCommunityDocument"
                            />
                        </div>
                    </tab-panel>
                    <tab-panel value="pageFeatures">
                        <div class="page-community-tab-content">
                            <document-items-list
                                v-if="pageCommunityTab === 'pageFeatures'"
                                :documents="pageFeatureDocuments"
                                list-layout="full"
                                :is-loading="isPageCommunityLoading"
                                :is-document-unread="isCommunityDocumentUnread"
                                :initial-visible-count="INITIAL_VISIBLE_DOCUMENT_COUNT"
                                :load-more-count="20"
                                document-route-name="deltaGptDocument"
                                open-mode="emit"
                                empty-message="No Community features are linked to this page yet."
                                @open-document="openPageCommunityDocument"
                            />
                        </div>
                    </tab-panel>
                    <tab-panel value="enactNotifications">
                        <div class="page-community-tab-content">
                            <enact-notifications-panel :active="pageCommunityTab === 'enactNotifications'" />
                        </div>
                    </tab-panel>
                </tab-panels>
            </tabs>
        </div>
    </central-modal>

    <central-modal
        v-model:visible="showPageCommunityDocumentModal"
        :header="selectedPageCommunityDocumentTitle"
        :size="{ width: '100rem', height: '95vh' }"
    >
        <spark-content-view-holder
            v-if="selectedPageCommunityDocument && isRichContentDocument(selectedPageCommunityDocument)"
            :document-short-code="selectedPageCommunityDocument.documentShortCode"
            display-mode="modal"
            no-breadcrumb
            :allow-comments-expand="false"
            @navigated="closeCommunityModalsAfterNavigation"
        />
        <document-view
            v-else-if="selectedPageCommunityDocument"
            :document-short-code="selectedPageCommunityDocument.documentShortCode"
            no-breadcrumb
            display-mode="modal"
            :allow-comments-expand="false"
        />
    </central-modal>

    <central-modal
        v-model:visible="showPageCommunityThreadEditorModal"
        header="Add Community thread"
        :size="{ width: '100rem', height: '95vh' }"
    >
        <community-thread-editor
            :key="`page-community-thread-editor-${currentRouteName}`"
            :is-saving="isSavingThread"
            :can-delete="false"
            @save="saveCommunityThread"
        />
    </central-modal>
</template>

<script setup lang="ts">
import {
    computed, nextTick, ref, watch,
} from 'vue';
import { authState } from '@lcp/http-auth';
import { useRoute } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import CentralModal from '@/components/common/CentralModal.vue';
import CommunityThreadEditor from '@/components/community/CommunityThreadEditor.vue';
import CountBadge from '@/components/common/CountBadge.vue';
import DocumentItemsList from '@/views/delta-gpt/DocumentItemsList.vue';
import DocumentView from '@/views/delta-gpt/DocumentView.vue';
import EnactNotificationsPanel from '@/components/powerMarkets/notifications/EnactNotificationsPanel.vue';
import PrimevueButton from 'primevue/button';
import SparkContentViewHolder from '@/components/deltaGpt/SparkContentViewHolder.vue';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import { ICommunityThreadSaveRequest } from '@/interfaces/community/ICommunityThread.ts';
import { IDocumentSimple } from '@/interfaces/deltaGpt/IDocument.ts';
import { useDeltaGptStore } from '@/stores/deltaGpt/deltaGptStore.ts';
import { useDeltaUserSettingsStore } from '@/stores/deltaGpt/deltaGptUserSettingsStore.ts';
import { useCommunityFeed } from '@/composables/community/useCommunityFeed.ts';
import { useEnactNotifications } from '@/composables/powerMarkets/useEnactNotifications.ts';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import { router } from '@/router.ts';
import { marketsRoutes } from '@/routes/marketsRoutes.ts';
import { SectionRouteNames } from '@/routes/sectionRoutes/sectionRouteNames.ts';
import { isRichContentDocument } from '@/utils/researchContent.ts';
import { createRichContentDocument } from '@/utils/communityThreadUtils.ts';
import { getSectionRouteForSiteAreaRelevance } from '@/utils/siteAreaRelevanceUtils.ts';
import {
    communityThreadMatchesRoute,
} from '@/utils/communityPageLinkRegistry.ts';

const INITIAL_VISIBLE_DOCUMENT_COUNT = 20;

const route = useRoute();
const toast = useToast();
const deltaGptStore = useDeltaGptStore();
const deltaUserSettingsStore = useDeltaUserSettingsStore();
const { isNewFeedDocument, sortCommunityDocuments } = useCommunityFeed();
const userAccessStore = useUserAccessStore();
const {
    markNotificationsSeen,
    notificationsDisabled,
    unseenNotifications,
} = useEnactNotifications();

const showPageCommunityModal = ref<boolean>(false);
const showPageCommunityThreadEditorModal = ref<boolean>(false);
const showPageCommunityDocumentModal = ref<boolean>(false);
const isPageCommunityLoading = ref<boolean>(false);
const isSavingThread = ref<boolean>(false);
const selectedPageCommunityDocumentShortCode = ref<string>('');
const pageCommunityTab = ref<'yourFeed' | 'pageFeed' | 'pageFeatures' | 'enactNotifications'>('yourFeed');
const hasViewedYourFeedInCommunityModal = ref<boolean>(false);
const hasViewedEnactNotificationsInCommunityModal = ref<boolean>(false);

const currentSection = computed(() => route.matched[0] ?? null);
const currentSectionRouteName = computed<string | null>(() => {
    const routeName = currentSection.value?.name;
    return typeof routeName === 'string' && Object.values(SectionRouteNames).includes(routeName as SectionRouteNames)
        ? routeName
        : null;
});
const currentRouteName = computed<string | null>(() => (typeof route.name === 'string' ? route.name : null));
const currentRouteTitle = computed<string>(() => String(route.meta.displayTitle ?? route.meta.headName ?? 'this page'));
const relevantCommunityPageRouteName = computed<string>(() => {
    const sectionRouteName = currentSection.value?.name;
    if (typeof sectionRouteName === 'string' && Object.values(SectionRouteNames).includes(sectionRouteName as SectionRouteNames)) {
        const sectionResearchRouteName = `${sectionRouteName}_deltaGptDocumentsList`;
        if (router.hasRoute(sectionResearchRouteName)) return sectionResearchRouteName;
    }

    return 'deltaGptDocumentsList';
});
const relevantCommunityPageLabel = computed<string>(() => (relevantCommunityPageRouteName.value === 'deltaGptDocumentsList' ? 'View all research' : 'View section research'));
const activePageCommunityDescription = computed<string>(() => {
    if (pageCommunityTab.value === 'yourFeed') return 'Content relevant to you.';
    if (pageCommunityTab.value === 'pageFeed') return `Content linked to ${currentRouteTitle.value}.`;
    if (pageCommunityTab.value === 'pageFeatures') return `Features linked to ${currentRouteTitle.value}.`;
    if (pageCommunityTab.value === 'enactNotifications') return 'Your latest Enact notifications.';
    return '';
});
const canViewCommunity = computed<boolean>(() => authState.value.loggedIn);
const pageCommunityDocuments = computed<Array<IDocumentSimple>>(() => deltaGptStore.allDocumentsSimple
    .filter(isRichContentDocument)
    .filter(document => richContentMatchesCurrentCommunityContext(document))
    .sort(sortCommunityDocuments));
const userCommunityDocuments = computed<Array<IDocumentSimple>>(() => deltaGptStore.allDocumentsSimple
    .filter(document => document.isRelevantToUser)
    .sort(sortCommunityDocuments));
const pageFeatureDocuments = computed<Array<IDocumentSimple>>(() => pageCommunityDocuments.value
    .filter(document => document.reportType === 'Feature'));
const newUserFeedDocumentCount = computed<number>(() => userCommunityDocuments.value.filter(isNewFeedDocument).length);
const newPageFeedDocumentCount = computed<number>(() => pageCommunityDocuments.value.filter(isNewFeedDocument).length);
const newPageFeatureDocumentCount = computed<number>(() => pageFeatureDocuments.value.filter(isNewFeedDocument).length);
const selectedPageCommunityDocument = computed<IDocumentSimple | null>(() => (
    [...pageCommunityDocuments.value, ...userCommunityDocuments.value]
        .find(document => document.documentShortCode === selectedPageCommunityDocumentShortCode.value) ?? null
));
const selectedPageCommunityDocumentTitle = computed<string>(() => selectedPageCommunityDocument.value?.documentTitle ?? 'Community content');

async function fetchCommunityThreadsIfNeeded (): Promise<void> {
    if (!canViewCommunity.value || deltaGptStore.allDocumentsSimple.length > 0) return;

    await deltaGptStore.fetchAllDocumentInfo();
}

async function open (tab: typeof pageCommunityTab.value = 'yourFeed'): Promise<void> {
    if (!canViewCommunity.value) return;
    showPageCommunityThreadEditorModal.value = false;
    pageCommunityTab.value = tab;
    showPageCommunityModal.value = true;

    isPageCommunityLoading.value = true;
    try {
        await fetchCommunityThreadsIfNeeded();
    } catch {
        toast.add({
            severity: 'error', summary: 'Error', detail: 'Could not load Community threads.', life: 5000,
        });
    } finally {
        isPageCommunityLoading.value = false;
    }
}

function closePageCommunityModal (): void {
    showPageCommunityThreadEditorModal.value = false;

    if (hasViewedYourFeedInCommunityModal.value) {
        deltaUserSettingsStore.markYourFeedVisited();
        hasViewedYourFeedInCommunityModal.value = false;
    }

    markViewedEnactNotificationsSeen();

    pageCommunityTab.value = 'yourFeed';
}

function markViewedEnactNotificationsSeen (): void {
    if (hasViewedEnactNotificationsInCommunityModal.value) {
        markNotificationsSeen();
        hasViewedEnactNotificationsInCommunityModal.value = false;
    }
}

function closeCommunityModalsAfterNavigation (): void {
    showPageCommunityDocumentModal.value = false;
    showPageCommunityModal.value = false;
    closePageCommunityModal();
}

function openCreateCommunityThread (): void {
    showPageCommunityThreadEditorModal.value = true;
}

function richContentMatchesCurrentCommunityContext (document: IDocumentSimple): boolean {
    if (!currentRouteName.value) return false;
    if (richContentMatchesCurrentSiteAreaRelevance(document)) return true;

    const pageLinks = (document.routeNames ?? []).map(routeName => ({ routeName }));
    return communityThreadMatchesRoute(pageLinks, currentRouteName.value);
}

function richContentMatchesCurrentSiteAreaRelevance (document: IDocumentSimple): boolean {
    return getCurrentSiteAreaRelevanceRouteNames().some(sectionRouteName => document.siteAreaRelevance.includes(sectionRouteName));
}

function getCurrentSiteAreaRelevanceRouteNames (): Array<SectionRouteNames> {
    return [
        getSectionRouteForSiteAreaRelevance(currentRouteName.value),
        getSectionRouteForSiteAreaRelevance(currentSectionRouteName.value),
    ].filter((sectionRouteName): sectionRouteName is SectionRouteNames => sectionRouteName !== null);
}

function openPageCommunityDocument (document: IDocumentSimple): void {
    selectedPageCommunityDocumentShortCode.value = document.documentShortCode;
    showPageCommunityDocumentModal.value = true;
    deltaUserSettingsStore.markDocumentViewed(document);
}

async function openDocumentFromAttention (document: IDocumentSimple): Promise<void> {
    pageCommunityTab.value = 'yourFeed';
    await open('yourFeed');
    await nextTick();
    openPageCommunityDocument(document);
}

async function openEnactNotifications (): Promise<void> {
    await open('enactNotifications');
}

async function saveCommunityThread (payload: ICommunityThreadSaveRequest): Promise<void> {
    isSavingThread.value = true;
    try {
        const documentShortCode = payload.documentShortCode ?? '';
        const richContent = createRichContentDocument(documentShortCode || crypto.randomUUID(), payload);

        if (payload.thumbnailFile) {
            await deltaGptApi.uploadSparkContentWithImage(richContent, payload.thumbnailFile);
        } else {
            await deltaGptApi.addOrUpdateSparkContentAdmin(richContent);
        }

        showPageCommunityThreadEditorModal.value = false;
        await deltaGptStore.fetchAllDocumentInfo();
        toast.add({
            severity: 'success', summary: 'Saved', detail: 'Community thread saved.', life: 3000,
        });
    } catch {
        toast.add({
            severity: 'error', summary: 'Error', detail: 'Could not save Community thread.', life: 5000,
        });
    } finally {
        isSavingThread.value = false;
    }
}

function isCommunityDocumentUnread (document: IDocumentSimple): boolean {
    return !deltaUserSettingsStore.isDocumentViewed(document);
}

function goToRelevantCommunityPage (): void {
    showPageCommunityModal.value = false;
    closePageCommunityModal();
    router.push({ name: relevantCommunityPageRouteName.value });
}

function goToNotificationsManager (): void {
    showPageCommunityModal.value = false;
    closePageCommunityModal();
    router.push({ name: marketsRoutes.notifications.name });
}

watch(() => [showPageCommunityModal.value, pageCommunityTab.value], () => {
    if (showPageCommunityModal.value && pageCommunityTab.value === 'yourFeed') {
        hasViewedYourFeedInCommunityModal.value = true;
    }
    if (showPageCommunityModal.value && pageCommunityTab.value === 'enactNotifications') {
        hasViewedEnactNotificationsInCommunityModal.value = true;
        markViewedEnactNotificationsSeen();
    }
});

fetchCommunityThreadsIfNeeded().catch(() => {
    // The modal fetch path shows the toast if the user explicitly opens notifications.
});

defineExpose({
    open,
    openDocumentFromAttention,
    openEnactNotifications,
});
</script>

<style scoped lang="scss">
.page-community-modal {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
    height: 100%;
    min-height: 0;
    padding: var(--largeSpacing);
}

.page-community-modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--largeSpacing);
}

.page-community-modal-heading {
    h2 {
        margin: 0 0 var(--extraSmallSpacing) 0;
    }

    p {
        margin: 0;
        color: var(--neutral700);
    }
}

.page-community-modal-actions {
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
}

.page-community-tabs {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
}

.community-tab-label {
    display: inline-flex;
    align-items: center;
    position: relative;
    padding: 0 var(--defaultSpacing);

    .count-badge,
    .community-tab-lock {
        position: absolute;
        top: -0.6rem;
        right: 0;
        transform: translateX(70%);
    }

    .community-tab-lock {
        color: var(--accentColour);
        font-size: var(--smallFontSize);
    }
}

.page-community-tabs :deep(.p-tabpanels),
.page-community-tabs :deep(.p-tabpanel) {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
}

.page-community-tabs :deep(.p-tabpanels) {
    padding: 0;
}

.page-community-tab-content {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    padding-top: var(--defaultSpacing);
}
</style>
